import { AxiosResponse } from "axios";
import HttpService from "src/services/httpService";
import RouteService from "src/services/routeService";
import { ApiResponse, ListResponse } from "src/types";

import { unpackError } from "./helpers";

interface RequestFetchLicensePayload {
    groupId: number;
    pageIndex?: number;
    pageSize?: number;
}
const requestFetchLicense = async (payload: RequestFetchLicensePayload) => {
    const endpoint = await RouteService.getLicensesRoute();
    return HttpService.get<ListResponse<API.License>>(endpoint, { params: payload });
};

interface FetchLicenseResponse {
    groupId: number;
    licenses: ListResponse<API.License>;
}
interface FetchLicensesPayload {
    groupId: number[];
    isPrefetch?: boolean;
    pageSize?: number;
    pageIndex?: number;
}
export const fetchLicenses = async (payload: FetchLicensesPayload): Promise<FetchLicenseResponse[]> => {
    const groupIds = Array.isArray(payload.groupId) ? payload.groupId : [payload.groupId];
    const requests = groupIds.map((id) =>
        requestFetchLicense({ groupId: id, pageSize: payload.pageSize, pageIndex: payload.pageIndex }).then(({ data }) => ({
            groupId: id,
            licenses: data,
        })),
    );
    const licenses = await Promise.all(requests);
    return licenses;
};

interface LicenseUpdatePayload {
    id: number;
    comment: string | null;
}
export const updateLicense = async ({ id, ...payload }: LicenseUpdatePayload) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
        if (value !== null) {
            formData.append(key, value);
        }
    });

    try {
        const endpoint = await RouteService.getLicensesRoute();
        await HttpService.patch<FormData, ApiResponse<API.License>>(endpoint + String(id), formData);
        return Promise.resolve();
    } catch (_error) {
        throw Error("Could not update the license");
    }
};

interface LicenseRevokePayload {
    licenseId: number;
    reason: string;
}

export const revokeLicense = async (payload: LicenseRevokePayload) => {
    try {
        const endpoint = await RouteService.getLicensesRoute();
        const response = await HttpService.delete<{ reason: string }, AxiosResponse<ApiResponse<API.License>>>(
            endpoint + `${payload.licenseId}/revoke`,
            { data: { reason: payload.reason } },
        );
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

interface LicenseGeneratePayload {
    groupId: number;
    grantCount: number;
}

export const generateLicenses = async (payload: LicenseGeneratePayload) => {
    try {
        const endpoint = await RouteService.getLicensesRoute();
        const response = await HttpService.post<LicenseGeneratePayload, AxiosResponse<ApiResponse<API.License[]>>>(
            endpoint,
            payload,
        );
        return response.data.model;
    } catch (error) {
        throw unpackError(error);
    }
};
